import { useQuery as useApolloQuery } from '@apollo/client';
import { useQuery } from '@tanstack/react-query';

import { useGetEquipmentByIdQuery as useGqlEquipmentById } from '@/app/services/equipment';
import { useGetStringMapsQuery as useGqlStringMaps } from '@/app/services/lookups';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { StringMapBase } from '@/types';
import { GET_VETERINARIANS } from '@/types.gql';

import {
  equipmentByIdOptions,
  incubatorsOptions,
  stringMapOptions,
  veterinariansOptions,
} from '.';
import { IncubatorResponse, StringMapItem, TechnicianItem } from './types';

const SEMEN_TYPE_OBJECT_TYPE_CODE = 10017;
const FLAG = '30892_lookups_graphql_to_rest';

export function useIncubators(siteId: string | null | undefined) {
  const { data } = useQuery({
    ...incubatorsOptions({ siteId: siteId! }),
    enabled: !!siteId,
  });

  return data as IncubatorResponse[] | undefined;
}

export function useSemenTypes(): StringMapBase[] | undefined {
  const { data } = useQuery(
    stringMapOptions({
      objectTypeCode: SEMEN_TYPE_OBJECT_TYPE_CODE,
      name: 'tog_sementype',
    }),
  );

  return data?.map((item) => ({
    key: Number(item.key),
    value: item.value,
  }));
}

export function useGetStringMapsQuery(params: {
  objectTypeCode: number;
  attributeName: string;
}): { data: StringMapItem[]; isLoading: boolean } {
  const isRest = useFeatureFlag(FLAG);

  const restResult = useQuery({
    ...stringMapOptions({
      objectTypeCode: params.objectTypeCode,
      name: params.attributeName,
    }),
    enabled: isRest,
  });

  const gqlResult = useGqlStringMaps(
    {
      objectTypeCode: params.objectTypeCode,
      attributeName: params.attributeName,
    },
    { skip: isRest },
  );

  if (isRest) {
    return {
      data: (restResult.data ?? []).map((item) => ({
        attributeValue: String(item.key),
        value: item.value,
      })),
      isLoading: restResult.isLoading,
    };
  }

  return {
    data: (gqlResult.data ?? []).map((item) => ({
      attributeValue: item.attributeValue,
      value: item.value,
    })),
    isLoading: gqlResult.isLoading,
  };
}

export function useGetVeterinariansQuery(): {
  data: TechnicianItem[];
  isLoading: boolean;
} {
  const isRest = useFeatureFlag(FLAG);

  const restResult = useQuery({
    ...veterinariansOptions(),
    enabled: isRest,
  });

  const gqlResult = useApolloQuery(GET_VETERINARIANS, {
    skip: isRest,
    fetchPolicy: 'cache-first',
  });

  if (isRest) {
    return {
      data: restResult.data ?? [],
      isLoading: restResult.isLoading,
    };
  }

  return {
    data: (gqlResult.data?.equipments?.items ?? []).map((item) => ({
      id: item.id,
      name: item.name,
    })),
    isLoading: gqlResult.loading,
  };
}

export function useGetEquipmentByIdQuery(id: string): {
  data: TechnicianItem | null | undefined;
  isLoading: boolean;
} {
  const isRest = useFeatureFlag(FLAG);

  const restResult = useQuery({
    ...equipmentByIdOptions(id),
    enabled: isRest && !!id,
  });

  const gqlResult = useGqlEquipmentById(id, { skip: isRest || !id });

  if (isRest) {
    return {
      data: restResult.data,
      isLoading: restResult.isLoading,
    };
  }

  const gqlEquipment = gqlResult.data;
  return {
    data: gqlEquipment
      ? { id: gqlEquipment.id, name: gqlEquipment.name }
      : undefined,
    isLoading: gqlResult.isLoading,
  };
}
